import { Button } from "@/src/components/buttons";
import { Card, CompartmentCard } from "@/src/components/cards";
import { closeDrawer, openDrawer } from "@/src/components/dialogs";
import { FetchableList } from "@/src/components/fetchableLists";
import { Input } from "@/src/components/universalInput";
import {
  OrdonnanceCategory,
  OrdonnanceTemplate,
  Protocol,
  ProtocolDay,
} from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import { getUserId, jsonAbstract, uid, updateOrPush } from "@/src/utils/utils";
import { useState } from "react";
import { openTemplateChoice } from "../../medicines/ordonnanceTemplateHook";
import { AppointmentTypePicker } from "./adminAppointmentTypes";

function AdminProtocols() {
  const [reload, setReload] = useState(0);
  let [searchKey, setSearchKey] = useState("");

  async function openProtocolDrawer(protocol: Protocol) {
    await openDrawer({
      id: "protocolDrawer",
      title: "Ajouter un protocol",
      content: (
        <ProtocolForm
          current={protocol}
          onChange={(newValue) => {
            protocol = newValue;
          }}
        />
      ),
      onConfirm: async () => {
        let inserted = await apiFetch("protocols", "addOne", {
          protocol,
        });
        setReload((prev) => prev + 1);
      },
    });
  }

  return (
    <CompartmentCard
      title={"Protocols"}
      searchKey={searchKey}
      onSearch={(newValue) => {
        setSearchKey(newValue || "");
      }}
      onClick={async () => {
        let protocol: Protocol = {
          name: searchKey,
          creator: USER?.get(),
          intercure: 21,
          numberOfCycles: 6,
          description: "",
          appointmentType: null,
          days: [
            {
              day: 0,
              templates: [],
              protocol: null,
              creator: getUserId(),
              
            },
          ],
        };
        await openProtocolDrawer(protocol);
      }}
    >
      <FetchableList
        queryKey={["AdminProtocols", searchKey, reload]}
        queryFn={() =>
          apiFetch("protocols", "loadMany", {
            searchKey,
          })
        }
      >
        {(data) => (
          <div className="grid c1 gap10">
            {data?.map((protocol) => {
              return (
                <Card
                  key={uid()}
                  color="purple"
                  title={protocol.name}
                  subTitle={
                    <div>
                      <div className="gray">{protocol.intercure} jours</div>
                      {protocol.description != null && (
                        <div className="gray">{protocol.description || ""}</div>
                      )}
                    </div>
                  }
                  onClick={async () => {
                    await openProtocolDrawer(protocol);
                  }}
                >
                  {protocol.days?.length != 0 && (
                    <div className="grid gap10 c8 blueish br p5">
                      {protocol.days.map((protocolDay) => {
                        return (
                          <div key={uid()} className=" card">
                            J{protocolDay?.day}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </FetchableList>
    </CompartmentCard>
  );
}

function ProtocolForm({
  current,
  onChange,
}: {
  current: Protocol;
  onChange: (newValue: Protocol) => void;
}) {
  let [protocol, setProtocol] = useState(current || ({} as Protocol));

  function setNewValue(newValue: Protocol) {
    setProtocol({ ...newValue });
    onChange({ ...newValue });
  }

  return (
    <div className="grid c1 gap10">
      <Input
        name={"Nom"}
        type={"text"}
        value={protocol.name}
        onChange={(newValue) => {
          setNewValue({ ...protocol, name: newValue });
        }}
      />
      <Input
        name={"Description"}
        type={"text"}
        value={protocol.description}
        onChange={(newValue) => {
          setNewValue({ ...protocol, description: newValue });
        }}
      />
      <Input
        name={"Intercure"}
        type={"select"}
        value={protocol.intercure?.toString()}
        options={Array.from({ length: 60 }, (_, i) => i + 1).map((day) => {
          return { value: day?.toString(), label: `${day} jours` };
        })}
        onChange={(newValue) => {
          setNewValue({ ...protocol, intercure: newValue });
        }}
      />
      <Input
        name={"Nombre de cures"}
        type={"select"}
        value={protocol.numberOfCycles?.toString()}
        options={Array.from({ length: 60 }, (_, i) => i + 1).map((day) => {
          return { value: day?.toString(), label: `${day} cures` };
        })}
        onChange={(newValue) => {
          setNewValue({ ...protocol, numberOfCycles: newValue });
        }}
      />
      <ReorderableProtocolDays
        label={"Jours"}
        protocol={protocol}
        current={protocol.days}
        onChange={(newValue) => {
          setNewValue({ ...protocol, days: newValue });
        }}
      />

      <AppointmentTypePicker
        title="Type de rendez-vous associé"
        value={protocol.appointmentType}
        onChange={(newValue) => {
          setNewValue({ ...protocol, appointmentType: newValue });
        }}
      ></AppointmentTypePicker>
    </div>
  );
}

function ReorderableProtocolDays({
  label,
  protocol,
  current,
  onChange,
}: {
  label?: string | null | undefined;
  protocol: Protocol;
  current: (ProtocolDay | null | undefined)[];
  onChange: (newValue: (ProtocolDay | null | undefined)[]) => void;
}) {
  let [protocolDays, setProtocolDays] = useState(current || []);
  let [reload, setReload] = useState(0);

  async function openNewItemDrawer(protocolDay: ProtocolDay) {
    await openDrawer({
      title: "ProtocolDay",
      content: (
        <ProtocolDayForm
          current={protocolDay}
          onChange={(newValue) => {
            protocolDay = newValue;
          }}
        />
      ),
      onConfirm: async () => {
        let newProtocolDays = [...protocolDays];

        newProtocolDays = updateOrPush(newProtocolDays, protocolDay, (item) => {
          return item?.day == protocolDay.day;
        });

        newProtocolDays = newProtocolDays.sort((a, b) => {
          return (a?.day || 0) - (b?.day || 0);
        });

        onChange(newProtocolDays);
        setProtocolDays(newProtocolDays);
        setReload(reload + 1);
      },
    });
  }

  return (
    <div className="c1 gap10">
      {label ? <label>{label}</label> : null}

      {protocolDays?.length != 0 && (
        <div className="c1 gap10">
          {protocolDays.map((protocolDay) => {
            return (
              <Card
                key={uid()}
                color="green"
                onDelete={() => {
                  let newProtocolDays = [...protocolDays];
                  newProtocolDays = newProtocolDays.filter((item) => {
                    return item?.day != protocolDay?.day;
                  });

                  onChange(newProtocolDays);
                  setProtocolDays(newProtocolDays);
                  setReload(reload + 1);
                }}
                onEdit={async () => {
                  await openNewItemDrawer(protocolDay || ({} as ProtocolDay));
                }}
              >
                <div className="c1 gap10">
                  <div>J {protocolDay?.day}</div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Button
        type={"tertiary"}
        onClick={async (e) => {
          let protocolDay: ProtocolDay = {
            day: 0,
            protocol: jsonAbstract(protocol.id),
            templates: [],
            creator: getUserId(),
            
          };

          await openNewItemDrawer(protocolDay);
        }}
      >
        Ajouter un jour
      </Button>
    </div>
  );
}

function ProtocolDayForm({
  current,
  onChange,
}: {
  current: ProtocolDay;
  onChange: (newValue: ProtocolDay) => void;
}) {
  let [protocolDay, setProtocolDay] = useState(current || ({} as ProtocolDay));

  function setNewValue(newValue: ProtocolDay) {
    setProtocolDay({ ...newValue });
    onChange({ ...newValue });
  }

  return (
    <div className="grid c1 gap10">
      <Input
        name={"Jour"}
        type={"select"}
        value={protocolDay.day?.toString()}
        options={Array.from({ length: 60 }, (_, i) => i).map((day) => {
          return { value: day?.toString(), label: "J " + day };
        })}
        onChange={(newValue) => {
          setNewValue({ ...protocolDay, day: newValue });
        }}
      />
      <ReorderableOrdonnanceTemplates
        label={"Templates"}
        categories={["hdjOncology", "hospitalOncology"]}
        current={protocolDay.templates}
        onChange={(newValue) => {
          setNewValue({ ...protocolDay, templates: newValue });
        }}
      />
    </div>
  );
}

function ReorderableOrdonnanceTemplates({
  label,
  current,
  categories,
  onChange,
}: {
  label?: string | null | undefined;
  categories?: OrdonnanceCategory[];
  current: (OrdonnanceTemplate | null | undefined)[];
  onChange: (newValue: (OrdonnanceTemplate | null | undefined)[]) => void;
}) {
  let [ordonnanceTemplates, setOrdonnanceTemplates] = useState(current || []);
  let [reload, setReload] = useState(0);

  return (
    <div className="grid c1 gap10">
      {label ? <label>{label}</label> : null}

      {ordonnanceTemplates?.length != 0 && (
        <div className="grid c1 gap10">
          {ordonnanceTemplates.map((ordonnanceTemplate) => {
            return (
              <Card
                key={uid()}
                color="green"
                onDelete={() => {
                  let newOrdonnanceTemplates = [...ordonnanceTemplates];
                  newOrdonnanceTemplates = newOrdonnanceTemplates.filter(
                    (item) => {
                      return item?.id != ordonnanceTemplate?.id;
                    }
                  );

                  onChange(newOrdonnanceTemplates);
                  setOrdonnanceTemplates(newOrdonnanceTemplates);
                  setReload(reload + 1);
                }}
              >
                <div className="grid c1 gap10">
                  <div>{ordonnanceTemplate?.name}</div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Button
        type={"tertiary"}
        onClick={async (e) => {
          await openTemplateChoice({
            categories,
            onSelect: (template) => {
              let newOrdonnanceTemplates = [...ordonnanceTemplates];
              newOrdonnanceTemplates = updateOrPush(
                newOrdonnanceTemplates,
                template,
                (item) => {
                  return item?.id == template.id;
                }
              );

              onChange(newOrdonnanceTemplates);
              setOrdonnanceTemplates(newOrdonnanceTemplates);
              setReload(reload + 1);
              closeDrawer("ordonnanceDrawer");
            },
          });
        }}
      >
        Ajouter un template
      </Button>
    </div>
  );
}

function ProtocolChoiceList({
  onSelect,
}: {
  onSelect: (newValue: Protocol) => void;
}) {
  const [reload, setReload] = useState(0);
  let [searchKey, setSearchKey] = useState("");

  async function openProtocolDrawer(protocol: Protocol | null) {
    protocol ||= {
      name: `${searchKey || ""}`,
      creator: getUserId(),
      days: [
        {
          day: 0,
          templates: [],
          protocol: null,
          creator: getUserId(),
          
        },
      ],
      description: "",
      numberOfCycles: 6,
      intercure: 21,
      appointmentType: null,
    };

    await openDrawer({
      id: "protocolDrawer",
      title: "Ajouter un protocol",
      content: (
        <ProtocolForm
          current={protocol}
          onChange={(newValue) => {
            protocol = newValue;
          }}
        />
      ),
      onConfirm: async () => {
        if (!protocol) return;
        let result = await apiFetch("protocols", "addOne", { protocol });
        if (!result) return;
        onSelect(result);
      },
    });
  }

  return (
    <div className="grid c1 gap10">
      <FetchableList
        queryKey={["ProtocolChoiceList", searchKey, reload]}
        queryFn={() => apiFetch("protocols", "loadMany", { searchKey })}
        searchKey={searchKey}
        onSearch={(newValue) => {
          setSearchKey(newValue || "");
        }}
        onClick={async () => {
          await openProtocolDrawer(null);
        }}
        emptyChild={
          searchKey && (
            <div className="center grid c1 gap10 p20">
              Aucun modèle disponible
              <Button
                onClick={async () => {
                  await openProtocolDrawer(null);
                }}
              >
                Créer le modèle {searchKey}
              </Button>
            </div>
          )
        }
      >
        {(data) => (
          <div className="grid c1 gap10">
            {data?.map((protocol, index) => {
              return (
                <Card
                  key={uid()}
                  onClick={async () => {
                    onSelect(jsonAbstract(protocol) as Protocol);
                  }}
                  color="purple"
                  title={protocol.name}
                  onEdit={async () => {
                    await openProtocolDrawer(protocol);
                    setReload((prev) => prev + 1);
                  }}
                >
                  <div className="gray">{protocol.description}</div>
                </Card>
              );
            })}
          </div>
        )}
      </FetchableList>
    </div>
  );
}

export { AdminProtocols, ProtocolChoiceList };
